/**
 * Debt — the loan register for one entity, each loan beside what its schedule says today.
 *
 * FINANCE.md §13. Every position figure (balance, next payment, maturity, balloon) comes from
 * `GET /api/loans`, computed by `amortizationSchedule` in packages/finance on each request and
 * never stored; this screen formats and adds nothing. The form takes the note as the lender
 * states it — dollars typed as text and converted with `dollarsToCents` (never through a float),
 * the rate as a percentage converted to integer basis points — and the API refuses terms the
 * schedule engine refuses, with the engine's own sentence.
 *
 * Owner only for the form; the API enforces it (a viewer gets a 403), the screen just does not
 * pretend otherwise.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dollarsToCents } from '@sixgen/core';
import { api, type LoansResponse, type Property } from '../../api.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';
import { formatMoney } from '../../money.js';

/** "6.25" -> 625 basis points, exactly, through the same string route dollars take. */
export function percentToBps(input: string): number {
  const cleaned = input.trim().replace(/[%\s]/g, '');
  const match = /^(\d*)(?:\.(\d{0,2}))?$/.exec(cleaned);
  if (!match || (match[1] === '' && (match[2] ?? '') === '')) {
    throw new RangeError(`Not a valid rate: ${JSON.stringify(input)}`);
  }
  const whole = match[1] === '' ? 0 : Number(match[1]);
  const frac = Number((match[2] ?? '').padEnd(2, '0') || '0');
  return whole * 100 + frac;
}

function bpsToPercent(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

export function DebtPage(): ReactNode {
  const entityId = useEntityId();
  const entities = useEntities();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [stored, setStored] = useState<string | null>(null);

  const loans = useQuery({
    queryKey: ['loans', entityId],
    enabled: entityId !== null,
    queryFn: () => api.get<LoansResponse>(`/api/loans?entity=${entityId}`),
  });
  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<{ properties: Property[] }>('/api/properties'),
  });

  const createLoan = useMutation({
    mutationFn: (body: unknown) => api.post<{ loan: { name: string } }>('/api/loans', body),
    onSuccess: (result) => {
      setError(null);
      setStored(`Recorded ${result.loan.name}.`);
      void queryClient.invalidateQueries({ queryKey: ['loans'] });
    },
    onError: (err: unknown) => {
      setStored(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  function submit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!entityId) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const text = (name: string): string => String(data.get(name) ?? '').trim();
    try {
      const payment = text('payment');
      const escrow = text('escrow');
      const property = text('property');
      createLoan.mutate({
        entityId,
        propertyId: property === '' ? null : property,
        lender: text('lender'),
        name: text('name'),
        principalCents: dollarsToCents(text('principal')).toString(),
        annualRateBps: percentToBps(text('rate')),
        termMonths: Number(text('term')),
        paymentCents: payment === '' ? null : dollarsToCents(payment).toString(),
        firstPaymentDate: text('firstPayment'),
        rateType: text('rateType') === 'variable' ? 'variable' : 'fixed',
        escrowCents: escrow === '' ? '0' : dollarsToCents(escrow).toString(),
        notes: text('notes') === '' ? null : text('notes'),
      });
      form.reset();
    } catch (err) {
      setStored(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;
  const data = loans.data;
  const entityProperties = (properties.data?.properties ?? []).filter(
    (p) => p.entity_id === entityId,
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Debt</h1>
          <p className="subtitle">
            Every loan as the note states it. The balance, the next payment and the balloon are
            computed from those terms on every read, never stored.
          </p>
        </div>
      </header>

      <div className="filters">
        <EntityPicker />
      </div>

      {(loans.isLoading || (entityId === null && entities.isLoading)) && (
        <p className="muted">Loading…</p>
      )}
      {loans.isError && <p className="error">Could not load loans: {loans.error.message}</p>}
      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {data && data.loans.length === 0 && (
        <div className="card empty">
          <h2>No loans recorded for this entity.</h2>
          <p>Record one below from the note: principal, rate, term, payment, first payment date.</p>
        </div>
      )}

      {data && data.loans.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Loan</th>
                <th>Lender</th>
                <th className="num">Principal</th>
                <th className="num">Rate</th>
                <th className="num">Payment</th>
                <th className="num">Balance {data.as_of}</th>
                <th>Next payment</th>
                <th>Matures</th>
                <th className="num">Balloon</th>
              </tr>
            </thead>
            <tbody>
              {data.loans.map(({ loan, position }) => (
                <tr key={loan.id}>
                  <td>
                    {loan.name}
                    {loan.status === 'paid_off' && <span className="chip-tag">paid off</span>}
                  </td>
                  <td className="muted">{loan.lender}</td>
                  <td className="num">{formatMoney(loan.principal_cents)}</td>
                  <td className="num">
                    {bpsToPercent(loan.annual_rate_bps)}
                    {loan.rate_type === 'variable' && <span className="muted small"> var.</span>}
                  </td>
                  <td className="num">
                    {formatMoney(position.payment_cents)}
                    {loan.escrow_cents !== '0' && (
                      <span className="muted small">
                        {' '}
                        + {formatMoney(loan.escrow_cents)} escrow
                      </span>
                    )}
                  </td>
                  <td className="num">
                    {position.paid_off ? (
                      <span className="muted">paid off</span>
                    ) : (
                      formatMoney(position.balance_cents)
                    )}
                  </td>
                  <td>
                    {position.next_payment ? (
                      <>
                        {position.next_payment.date}{' '}
                        <span className="muted small">
                          ({formatMoney(position.next_payment.principal_cents)} principal,{' '}
                          {formatMoney(position.next_payment.interest_cents)} interest)
                        </span>
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{position.maturity_date}</td>
                  <td className="num">
                    {position.balloon_cents === '0' ? (
                      <span className="muted">—</span>
                    ) : (
                      formatMoney(position.balloon_cents)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {entityId && (
        <section className="card">
          <h2>Record a loan</h2>
          <p className="muted small">
            From the note. Dollars as written; the rate as a percentage; the payment is principal
            and interest, and may be left blank to use the annuity payment over the term.
          </p>
          <form className="stack" onSubmit={submit}>
            <div className="field-row">
              <label>
                Name
                <input name="name" required maxLength={200} placeholder="Sketch Rd mortgage" />
              </label>
              <label>
                Lender
                <input name="lender" required maxLength={200} placeholder="Central Bank" />
              </label>
              <label>
                Property
                <select name="property" defaultValue="">
                  <option value="">— none / entity-level —</option>
                  {entityProperties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="field-row">
              <label>
                Principal ($)
                <input name="principal" required inputMode="decimal" placeholder="250,000.00" />
              </label>
              <label>
                Rate (% per year)
                <input name="rate" required inputMode="decimal" placeholder="6.25" />
              </label>
              <label>
                Term (months)
                <input name="term" required type="number" min={1} max={600} placeholder="360" />
              </label>
              <label>
                Rate type
                <select name="rateType" defaultValue="fixed">
                  <option value="fixed">fixed</option>
                  <option value="variable">variable</option>
                </select>
              </label>
            </div>
            <div className="field-row">
              <label>
                Monthly payment ($, P&amp;I)
                <input name="payment" inputMode="decimal" placeholder="blank = annuity payment" />
              </label>
              <label>
                Escrow per month ($)
                <input name="escrow" inputMode="decimal" placeholder="0.00" />
              </label>
              <label>
                First payment date
                <input name="firstPayment" required type="date" />
              </label>
            </div>
            <label>
              Notes
              <input name="notes" maxLength={2000} />
            </label>
            <div>
              <button type="submit" disabled={createLoan.isPending}>
                {createLoan.isPending ? 'Recording…' : 'Record loan'}
              </button>
            </div>
          </form>
          {stored && <p className="muted">{stored}</p>}
          {error && <p className="error">{error}</p>}
        </section>
      )}
    </div>
  );
}
